<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div>
    <!-- Toolbar -->
    <div class="view-toolbar">
      <PrimeButton
        :label="t('adminViewList.importView.button')"
        icon="ri-upload-line"
        severity="secondary"
        outlined
        @click="triggerImport"
      />
      <PrimeButton
        :label="t('adminViewList.exportAll.button')"
        icon="ri-download-line"
        severity="secondary"
        outlined
        :disabled="views.length === 0"
        @click="exportAllAsZip"
      />
      <input
        ref="fileInput"
        type="file"
        accept=".json"
        multiple
        style="display: none"
        @change="handleFileImport"
      />
    </div>

    <!-- Drop zone -->
    <div
      class="drop-zone"
      :class="{ 'drop-zone--active': isDragOver }"
      @dragover.prevent="isDragOver = true"
      @dragleave.prevent="isDragOver = false"
      @drop.prevent="handleDrop"
    >
      <i class="ri-drag-drop-line drop-zone-icon" aria-hidden="true"></i>
      <span class="drop-zone-text">{{ t("adminViewList.dropZone.text") }}</span>
      <span class="drop-zone-hint">{{ t("adminViewList.dropZone.hint") }}</span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center p-3">
      <i class="ri-loader-4-line ri-spin" aria-hidden="true"></i>
      <span>{{ t("adminViewList.existingViews.loading") }}</span>
    </div>

    <!-- View table with selection bar + pagination -->
    <template v-else-if="views.length > 0">
      <!-- Selection bar -->
      <div v-if="selectedNodes.length > 0" class="selection-bar">
        <div class="selection-info">
          <span class="selection-text">
            {{
              t("adminViewList.selectionBar.selected", {
                count: selectedNodes.length,
                total: views.length,
              })
            }}
          </span>
          <span
            v-if="!isAllSelected"
            class="select-all-link"
            @click="selectAll"
          >
            {{
              t("adminViewList.selectionBar.selectAll", {
                total: views.length,
              })
            }}
          </span>
          <span v-else class="select-all-link" @click="selectedNodes = []">
            {{ t("adminViewList.selectionBar.clearSelection") }}
          </span>
        </div>

        <div class="selection-actions">
          <!-- Download selected -->
          <PrimeButton
            v-tooltip.top="t('adminViewList.selectionBar.downloadAll')"
            icon="ri-download-line"
            rounded
            text
            severity="secondary"
            @click="downloadSelected"
          />

          <div class="action-separator" />

          <!-- Delete selected -->
          <PrimeButton
            v-if="canDeleteSelected"
            v-tooltip.top="t('adminViewList.deleteView.button')"
            icon="ri-delete-bin-line"
            rounded
            text
            severity="secondary"
            @click="deleteSelected"
          />
        </div>
      </div>

      <ContentCompactTableView
        :files="paginatedViews"
        :current-node="{ id: 'view-list' }"
        :datatable-column-config="columnConfig"
        :inline-actions="inlineActions"
        :selected-nodes="selectedNodes"
        hide-more-actions
        @update:selected-nodes="selectedNodes = $event"
        @navigate-to="(node) => emit('load-view', node.name)"
      />

      <!-- Paginator -->
      <PrimePaginator
        v-if="views.length > rowsPerPage"
        :first="first"
        :rows="rowsPerPage"
        :total-records="views.length"
        :rows-per-page-options="[10, 25, 50]"
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        @page="onPageChange"
      />
    </template>

    <!-- Empty state -->
    <div v-else class="p-3">
      <p>{{ t("adminViewList.existingViews.noViewsFound") }}</p>
    </div>

    <!-- Confirm dialog for delete -->
    <PrimeConfirmDialog group="deleteView" />

    <!-- Edit details dialog -->
    <PrimeDialog
      v-model:visible="showEditDialog"
      :header="t('adminViewList.editView.title')"
      modal
      :style="{ width: '95vw', maxWidth: '30rem' }"
    >
      <div class="flex flex-column gap-3">
        <div class="flex flex-column gap-1">
          <label for="edit-view-name" class="font-semibold">{{
            t("adminViewList.editView.name")
          }}</label>
          <PrimeInputText
            id="edit-view-name"
            v-model="editName"
            :placeholder="t('adminViewList.editView.namePlaceholder')"
          />
        </div>
        <div class="flex flex-column gap-1">
          <label for="edit-view-description" class="font-semibold">{{
            t("adminViewList.editView.description")
          }}</label>
          <PrimeTextarea
            id="edit-view-description"
            v-model="editDescription"
            :placeholder="t('adminViewList.editView.descriptionPlaceholder')"
            rows="3"
            auto-resize
          />
        </div>
      </div>
      <template #footer>
        <PrimeButton
          :label="t('adminViewList.editView.cancel')"
          severity="secondary"
          text
          @click="showEditDialog = false"
        />
        <PrimeButton
          :label="t('adminViewList.editView.save')"
          icon="ri-save-line"
          :disabled="!editName"
          @click="saveEditDetails"
        />
      </template>
    </PrimeDialog>
  </div>
</template>

<script setup>
import { ref, computed, provide } from "vue";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { useI18n } from "vue-i18n";
import PrimeButton from "primevue/button";
import PrimeConfirmDialog from "primevue/confirmdialog";
import PrimeDialog from "primevue/dialog";
import PrimeInputText from "primevue/inputtext";
import PrimeTextarea from "primevue/textarea";
import PrimePaginator from "primevue/paginator";
import Tooltip from "primevue/tooltip";
import ContentCompactTableView from "../navigation/ContentCompactTableView.vue";
import viewConfigService from "@/services/ViewConfigService.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";

const vTooltip = Tooltip;

const props = defineProps({
  views: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(["load-view", "views-changed"]);

const toast = useToast();
const confirm = useConfirm();
const { t } = useI18n();

const fileInput = ref(null);
const selectedNodes = ref([]);
const isDragOver = ref(false);

// Edit details dialog
const showEditDialog = ref(false);
const editingNode = ref(null);
const editName = ref("");
const editDescription = ref("");

// Pagination
const first = ref(0);
const rowsPerPage = ref(10);

const paginatedViews = computed(() => {
  return props.views.slice(first.value, first.value + rowsPerPage.value);
});

const onPageChange = (event) => {
  first.value = event.first;
  rowsPerPage.value = event.rows;
};

// Required by ContentCompactTableView (injected dependency)
provide("isCurrentNodeDisplayed", () => false);

const columnConfig = [
  {
    key: "name",
    label: t("adminViewList.existingViews.title"),
    visible: true,
  },
];

// --- Single-row inline actions ---

const downloadView = (event, node) => {
  viewConfigService
    .getView(node.name)
    .then((jsonContent) => {
      const blob = new Blob([JSON.stringify(jsonContent, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = node.name;
      link.click();
      URL.revokeObjectURL(url);
    })
    .catch((err) => {
      console.error("Failed to download view", err);
      toast.add({
        severity: "error",
        summary: t("adminViewList.toast.downloadError.summary"),
        detail: t("adminViewList.toast.downloadError.detail"),
        life: 4000,
      });
    });
};

const deleteView = (event, node) => {
  confirm.require({
    message: t("adminViewList.deleteView.confirmMessage", {
      name: node.name,
    }),
    header: t("adminViewList.deleteView.confirmHeader"),
    icon: "ri-error-warning-line",
    group: "deleteView",
    acceptProps: { severity: "danger" },
    accept: () => {
      alfrescoNodeService
        .deleteNode(node.id, "fichier")
        .then(() => {
          toast.add({
            severity: "success",
            summary: t("adminViewList.deleteView.toast.success.summary"),
            detail: t("adminViewList.deleteView.toast.success.detail", {
              name: node.name,
            }),
            life: 3000,
          });
          selectedNodes.value = selectedNodes.value.filter(
            (n) => n.id !== node.id,
          );
          emit("views-changed");
        })
        .catch((err) => {
          console.error("Failed to delete view", err);
          toast.add({
            severity: "error",
            summary: t("adminViewList.deleteView.toast.error.summary"),
            detail: t("adminViewList.deleteView.toast.error.detail"),
            life: 4000,
          });
        });
    },
  });
};

// --- Edit details ---

const openEditDialog = async (event, node) => {
  editingNode.value = node;
  try {
    const content = await viewConfigService.getView(node.name);
    editName.value = content.name || node.name.replace(/^vue-|\.json$/g, "");
    editDescription.value = content.description || "";
  } catch {
    editName.value = node.name.replace(/^vue-|\.json$/g, "");
    editDescription.value = "";
  }
  showEditDialog.value = true;
};

const saveEditDetails = async () => {
  if (!editingNode.value || !editName.value) {
    return;
  }
  try {
    const content = await viewConfigService.getView(editingNode.value.name);
    content.name = editName.value;
    content.description = editDescription.value;
    await viewConfigService.saveView(editingNode.value.name, content);
    toast.add({
      severity: "success",
      summary: t("adminViewList.editView.toast.success.summary"),
      detail: t("adminViewList.editView.toast.success.detail"),
      life: 3000,
    });
    showEditDialog.value = false;
    emit("views-changed");
  } catch (err) {
    console.error("Failed to update view details", err);
    toast.add({
      severity: "error",
      summary: t("adminViewList.editView.toast.error.summary"),
      detail: t("adminViewList.editView.toast.error.detail"),
      life: 4000,
    });
  }
};

const inlineActions = [
  {
    icon: "ri-pencil-line",
    tooltip: t("adminViewList.editView.button"),
    handler: openEditDialog,
  },
  {
    icon: "ri-download-line",
    tooltip: t("adminViewList.existingViews.download"),
    handler: downloadView,
  },
  {
    icon: "ri-delete-bin-line",
    tooltip: t("adminViewList.deleteView.button"),
    severity: "danger",
    handler: deleteView,
    visible: (node) => node.allowableOperations?.includes("delete"),
  },
];

// --- Selection bar bulk actions ---

const isAllSelected = computed(() => {
  return (
    selectedNodes.value.length === props.views.length && props.views.length > 0
  );
});

const selectAll = () => {
  selectedNodes.value = [...props.views];
};

const canDeleteSelected = computed(() => {
  return (
    selectedNodes.value.length > 0 &&
    selectedNodes.value.every((node) =>
      node.allowableOperations?.includes("delete"),
    )
  );
});

const downloadSelected = () => {
  selectedNodes.value.forEach((node) => {
    downloadView(null, node);
  });
};

const deleteSelected = () => {
  const names = selectedNodes.value.map((n) => n.name).join(", ");
  confirm.require({
    message: t("adminViewList.deleteView.confirmMessageMultiple", {
      count: selectedNodes.value.length,
      names,
    }),
    header: t("adminViewList.deleteView.confirmHeader"),
    icon: "ri-error-warning-line",
    group: "deleteView",
    acceptProps: { severity: "danger" },
    accept: () => {
      const deletePromises = selectedNodes.value.map((node) =>
        alfrescoNodeService.deleteNode(node.id, "fichier"),
      );
      Promise.allSettled(deletePromises).then((results) => {
        const successes = results.filter(
          (r) => r.status === "fulfilled",
        ).length;
        const failures = results.filter((r) => r.status === "rejected").length;
        if (successes > 0) {
          toast.add({
            severity: "success",
            summary: t("adminViewList.deleteView.toast.success.summary"),
            detail: t("adminViewList.deleteView.toast.successMultiple.detail", {
              count: successes,
            }),
            life: 3000,
          });
        }
        if (failures > 0) {
          toast.add({
            severity: "error",
            summary: t("adminViewList.deleteView.toast.error.summary"),
            detail: t("adminViewList.deleteView.toast.errorMultiple.detail", {
              count: failures,
            }),
            life: 4000,
          });
        }
        selectedNodes.value = [];
        emit("views-changed");
      });
    },
  });
};

// --- Export all as ZIP ---

const exportAllAsZip = async () => {
  try {
    const { default: JSZip } = await import("jszip");
    const zip = new JSZip();

    const downloadPromises = props.views.map(async (node) => {
      const jsonContent = await viewConfigService.getView(node.name);
      zip.file(node.name, JSON.stringify(jsonContent, null, 2));
    });

    await Promise.all(downloadPromises);

    const blob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "vues.zip";
    link.click();
    URL.revokeObjectURL(url);

    toast.add({
      severity: "success",
      summary: t("adminViewList.exportAll.toast.success.summary"),
      detail: t("adminViewList.exportAll.toast.success.detail", {
        count: props.views.length,
      }),
      life: 3000,
    });
  } catch (err) {
    console.error("Failed to export views as ZIP", err);
    toast.add({
      severity: "error",
      summary: t("adminViewList.exportAll.toast.error.summary"),
      detail: t("adminViewList.exportAll.toast.error.detail"),
      life: 4000,
    });
  }
};

// --- Import ---

const triggerImport = () => {
  fileInput.value.click();
};

const handleFileImport = (event) => {
  const files = Array.from(event.target.files);
  if (files.length === 0) {
    return;
  }
  importFiles(files);
  event.target.value = "";
};

const handleDrop = (event) => {
  isDragOver.value = false;
  const files = Array.from(event.dataTransfer.files).filter((f) =>
    f.name.endsWith(".json"),
  );
  if (files.length === 0) {
    toast.add({
      severity: "warn",
      summary: t("adminViewList.importView.toast.noJsonFiles.summary"),
      detail: t("adminViewList.importView.toast.noJsonFiles.detail"),
      life: 4000,
    });
    return;
  }
  importFiles(files);
};

const importFiles = async (files) => {
  let successCount = 0;
  let errorCount = 0;

  for (const file of files) {
    try {
      const text = await file.text();
      const importedData = JSON.parse(text);

      let filename = file.name.endsWith(".json")
        ? file.name
        : `${file.name}.json`;
      if (!filename.startsWith("vue-")) {
        filename = `vue-${filename}`;
      }

      await viewConfigService.saveView(filename, importedData);
      successCount++;
    } catch (err) {
      console.error(`Failed to import ${file.name}`, err);
      errorCount++;
    }
  }

  if (successCount > 0) {
    toast.add({
      severity: "success",
      summary: t("adminViewList.importView.toast.success.summary"),
      detail: t("adminViewList.importView.toast.success.detail", {
        count: successCount,
      }),
      life: 3000,
    });
  }
  if (errorCount > 0) {
    toast.add({
      severity: "error",
      summary: t("adminViewList.importView.toast.error.summary"),
      detail: t("adminViewList.importView.toast.error.detail", {
        count: errorCount,
      }),
      life: 4000,
    });
  }
  emit("views-changed");
};
</script>

<style lang="scss" scoped>
.view-toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.drop-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 1.25rem;
  margin-bottom: 0.75rem;
  border: 2px dashed var(--p-surface-border);
  border-radius: 0.75rem;
  background: var(--p-surface-ground);
  transition:
    border-color 0.2s,
    background 0.2s;
  cursor: pointer;

  &--active {
    border-color: var(--p-primary-color);
    background: color-mix(
      in srgb,
      var(--p-primary-color) 8%,
      var(--p-surface-ground)
    );
  }
}

.drop-zone-icon {
  font-size: 1.5rem;
  color: var(--p-text-muted-color);
}

.drop-zone-text {
  font-weight: 500;
  color: var(--p-text-color);
  font-size: 0.875rem;
}

.drop-zone-hint {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
}

.selection-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;
  background: color-mix(
    in srgb,
    var(--p-primary-color) 15%,
    var(--p-content-background)
  );
  border-left: 4px solid var(--p-primary-color);
  border-radius: 0.5rem;
  gap: 1rem;
}

.selection-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.selection-text {
  font-weight: 500;
  color: var(--p-text-color);
}

.select-all-link {
  font-weight: 600;
  color: var(--p-primary-color);
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--p-primary-hover-color);
  }
}

.selection-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.action-separator {
  width: 1px;
  height: 1.5rem;
  background-color: var(--p-surface-border);
  margin: 0 0.5rem;
}
</style>
